/**
 * Team Spain v0 — Módulo Principal de Formularios, Seguimiento y Salud (M6)
 * 
 * Reglas de diseño y seguridad:
 * - Adapta las pestañas y vistas según el rol autenticado: Atleta, Entrenador (Coach), Sanitario (Medic).
 * - El jugador gestiona sus pendientes, sesiones de entrenamiento, perfil e incidencias.
 * - El entrenador consulta cumplimiento de equipo, carga sRPE deportiva y disponibilidad operativa (cero datos clínicos).
 * - El sanitario dispone de bandeja de entrada, solicitudes de contacto, episodios abiertos, notas internas y evolución de 8 semanas.
 */

import React, { useState, useEffect, useMemo } from 'react'
import {
  ClipboardList,
  Dumbbell,
  HeartPulse,
  Activity,
  UserCheck,
  ShieldAlert,
  CheckCircle2,
  Clock,
  Plus,
  MessageSquare,
  TrendingUp,
  Settings,
  Utensils,
} from 'lucide-react'
import { formsService } from './formsService'
import { useAuth } from '../auth/AuthContext'
import {
  FormObligation,
  TrainingLogItem,
  FormResponseItem,
  WeeklyFormPayload,
  HealthIncidentItem,
  AvailabilityAssessmentItem,
  ContactRequestItem,
  TeamComplianceItem,
  AthleteProfileData,
  NUTRITION_QUALITY_LABELS,
  WATER_INTAKE_LABELS,
  PROTEIN_FREQUENCY_LABELS,
  FRUIT_VEG_SERVINGS_LABELS,
  POST_WORKOUT_INTAKE_LABELS,
  CampDailyComplianceItem,
  CampDailyMedicalInboxItem,
  CampDailyTrendItem,
  FormResponseRecord,
  SLEEP_QUALITY_LABELS,
  FATIGUE_LABELS,
  RECOVERY_LABELS,
  DISCOMFORT_STATUS_LABELS,
} from '../../types/health'
import { TrainingLogModal } from './TrainingLogModal'
import { IncidentReportModal } from './IncidentReportModal'
import { WeeklyFormModal } from './WeeklyFormModal'
import { MedicAssessmentModal } from './MedicAssessmentModal'
import { AthleteProfileModal } from './AthleteProfileModal'
import { TeamScheduleModal } from './TeamScheduleModal'
import { CampDailyCheckinModal } from './CampDailyCheckinModal'

interface HealthViewProps {
  teamId?: string
  athleteId?: string
}

export const HealthView: React.FC<HealthViewProps> = ({
  teamId: propTeamId,
  athleteId: propAthleteId,
}) => {
  const { user, isMedic: checkMedic, isCoach: checkCoach, hasRole } = useAuth()
  const teamId =
    propTeamId ||
    user?.memberships?.find((m) => m.team_id)?.team_id ||
    '33333333-3333-3333-3333-333333333333'

  const isMedic = checkMedic ? checkMedic() : Boolean(
    user?.memberships?.some((m) => m.roles.some((r) => ['medic', 'physio', 'doctor'].includes(r)))
  )
  const isCoach = checkCoach ? checkCoach() : Boolean(
    user?.memberships?.some((m) => m.roles.some((r) => ['coach', 'admin'].includes(r)))
  )
  const isAthlete = hasRole ? hasRole('athlete') : (!isMedic && !isCoach)

  // Pestañas según perfil
  type AthleteTab = 'obligations' | 'training' | 'incidents' | 'profile'
  type StaffTab = 'compliance' | 'workload' | 'availability' | 'inbox' | 'trends'

  const [athleteTab, setAthleteTab] = useState<AthleteTab>('obligations')
  const [staffTab, setStaffTab] = useState<StaffTab>(isMedic ? 'inbox' : 'compliance')

  // Estado del Atleta
  const [athleteId, setAthleteId] = useState<string | null>(
    propAthleteId || null
  )
  const [athleteProfile, setAthleteProfile] = useState<AthleteProfileData | null>(null)
  const [obligations, setObligations] = useState<FormObligation[]>([])
  const [trainingLogs, setTrainingLogs] = useState<TrainingLogItem[]>([])
  const [formResponses, setFormResponses] = useState<FormResponseItem[]>([])
  const [incidents, setIncidents] = useState<HealthIncidentItem[]>([])
  const [assessments, setAssessments] = useState<AvailabilityAssessmentItem[]>([])

  // Estado del Staff
  const [compliancePeriod, setCompliancePeriod] = useState<string>('2026-10-12')
  const [teamCompliance, setTeamCompliance] = useState<TeamComplianceItem[]>([])
  const [teamLogs, setTeamLogs] = useState<TrainingLogItem[]>([])
  const [contactRequests, setContactRequests] = useState<ContactRequestItem[]>([])
  const [allIncidents, setAllIncidents] = useState<HealthIncidentItem[]>([])
  const [selectedTrendAthleteId, setSelectedTrendAthleteId] = useState<string>(
    'a1111111-0000-0000-0000-000000000001'
  )
  const [trendAthleteResponses, setTrendAthleteResponses] = useState<FormResponseItem[]>([])

  // Modales
  const [trainingModalOpen, setTrainingModalOpen] = useState(false)
  const [selectedTrainingLog, setSelectedTrainingLog] = useState<TrainingLogItem | null>(null)
  const [incidentModalOpen, setIncidentModalOpen] = useState(false)
  const [weeklyModalOpen, setWeeklyModalOpen] = useState(false)
  const [activeWeeklyObligation, setActiveWeeklyObligation] = useState<FormObligation | null>(null)
  const [medicModalOpen, setMedicModalOpen] = useState(false)
  const [selectedAssessmentAthlete, setSelectedAssessmentAthlete] = useState<{
    id: string
    name: string
    dorsal?: number | null
    assessment?: AvailabilityAssessmentItem | null
    incident?: HealthIncidentItem | null
  } | null>(null)
  const [profileModalOpen, setProfileModalOpen] = useState(false)
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false)

  // Estados M8D — Check-in Diario de Concentración
  const [campDailyModalOpen, setCampDailyModalOpen] = useState(false)
  const [activeDailyObligation, setActiveDailyObligation] = useState<FormObligation | null>(null)
  const [activeDailyResponse, setActiveDailyResponse] = useState<FormResponseRecord | null>(null)

  const [complianceType, setComplianceType] = useState<'weekly' | 'camp_daily'>('weekly')
  const [inboxType, setInboxType] = useState<'general' | 'camp_daily'>('general')
  const [trendType, setTrendType] = useState<'weekly' | 'camp_daily'>('weekly')

  const [selectedCampId, setSelectedCampId] = useState<string>('e0000000-0000-0000-0000-000000000001')
  const [selectedCampDate, setSelectedCampDate] = useState<string>('2026-05-15')
  const [campComplianceList, setCampComplianceList] = useState<CampDailyComplianceItem[]>([])
  const [campMedicalInboxList, setCampMedicalInboxList] = useState<CampDailyMedicalInboxItem[]>([])
  const [campTrendsList, setCampTrendsList] = useState<CampDailyTrendItem[]>([])
  const [isLoadingCampStaff, setIsLoadingCampStaff] = useState<boolean>(false)

  const [isLoading, setIsLoading] = useState(true)
  const [notificationMsg, setNotificationMsg] = useState<string | null>(null)

  // 1. Cargar atleta autenticado
  useEffect(() => {
    let isMounted = true
    async function initAthlete() {
      if (!user) {
        if (isMounted) setAthleteId(null)
        return
      }
      try {
        const sb = (await import('../../lib/supabase')).supabase
        if (sb && isMounted) {
          const { data: ath } = await sb
            .from('athletes')
            .select('id')
            .eq('user_id', user.id)
            .maybeSingle()

          if (!isMounted) return
          if (ath?.id) {
            setAthleteId(ath.id)
            return
          }
        }
        if (isMounted) {
          // Si no hay conexión o en tests sin mock de supabase, solo demo.player tiene ficha
          if (user.email === 'demo.player@teamspain.demo') {
            setAthleteId('a1111111-0000-0000-0000-000000000001')
          } else {
            setAthleteId(null)
          }
        }
      } catch (err) {
        if (!isMounted) return
        console.error('Error inicializando atleta:', err)
        if (user.email === 'demo.player@teamspain.demo') {
          setAthleteId('a1111111-0000-0000-0000-000000000001')
        } else {
          setAthleteId(null)
        }
      }
    }
    initAthlete()
    return () => {
      isMounted = false
    }
  }, [user])

  // 2. Cargar datos según perfil
  const loadData = async () => {
    setIsLoading(true)
    try {
      if (athleteId) {
        // Datos de atleta
        const [prof, obls, logs, resps, incs, assess] = await Promise.all([
          formsService.getAthleteProfile(athleteId),
          formsService.getAthletePendingObligations(teamId, athleteId),
          formsService.getTrainingLogs(teamId, athleteId),
          formsService.getFormResponses(teamId, athleteId),
          formsService.getHealthIncidents(teamId, athleteId),
          formsService.getAvailabilityAssessments(teamId, athleteId),
        ])
        setAthleteProfile(prof || null)
        setObligations(obls || [])
        setTrainingLogs(logs || [])
        setFormResponses(resps || [])
        setIncidents(incs || [])
        setAssessments(assess || [])
      } else {
        setAthleteProfile(null)
        setObligations([])
        setTrainingLogs([])
        setFormResponses([])
        setIncidents([])
      }

      // Si es coach o medic, cargar datos de equipo
      if (isCoach || isMedic) {
        const [comp, tLogs, assessList] = await Promise.all([
          formsService.getTeamCompliance(teamId, compliancePeriod),
          formsService.getTrainingLogs(teamId),
          formsService.getAvailabilityAssessments(teamId),
        ])
        setTeamCompliance(comp || [])
        setTeamLogs(tLogs || [])
        setAssessments(assessList || [])

        if (isMedic) {
          const [contacts, allIncs] = await Promise.all([
            formsService.getContactRequests(teamId),
            formsService.getHealthIncidents(teamId),
          ])
          setContactRequests(contacts || [])
          setAllIncidents(allIncs || [])
        }
      }
    } catch (err: unknown) {
      console.error('Error cargando módulo de salud:', err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, athleteId, compliancePeriod, isCoach, isMedic])

  // Agregados semanales de entrenamiento del atleta
  const athleteWeeklyAggregates = useMemo(() => {
    return formsService.calculateWeeklyAggregates(trainingLogs)
  }, [trainingLogs])

  // Agregados de tendencias para el sanitario
  const trendAthleteLogs = useMemo(() => {
    return teamLogs.filter((l) => l.athlete_id === selectedTrendAthleteId)
  }, [teamLogs, selectedTrendAthleteId])

  const trendWeeklyAggregates = useMemo(() => {
    return formsService.calculateWeeklyAggregates(trendAthleteLogs)
  }, [trendAthleteLogs])

  // Cargar respuestas de formularios semanales del atleta para el sanitario
  useEffect(() => {
    if (!isMedic || staffTab !== 'trends') return
    formsService
      .getFormResponses(teamId, selectedTrendAthleteId)
      .then((res) => setTrendAthleteResponses(res))
      .catch((err) => console.error('Error cargando respuestas de tendencias para sanitario:', err))
  }, [isMedic, staffTab, teamId, selectedTrendAthleteId])

  // Valoración operativa vigente del atleta
  const currentAssessment = assessments[0] || null

  const handleOpenWeeklyForm = (obl: FormObligation) => {
    setActiveWeeklyObligation(obl)
    setWeeklyModalOpen(true)
  }

  const handleWeeklySuccess = (status: 'draft' | 'submitted') => {
    setNotificationMsg(
      status === 'submitted'
        ? '¡Formulario enviado correctamente! Ha quedado registrado con éxito.'
        : 'Borrador guardado online. Podrás continuar rellenándolo cuando lo desees.'
    )
    setTimeout(() => setNotificationMsg(null), 4000)
    loadData()
  }

  // Carga reactiva de datos de concentración para staff
  const loadCampStaffData = React.useCallback(async () => {
    if (!isCoach && !isMedic) return
    setIsLoadingCampStaff(true)
    try {
      if (staffTab === 'compliance' && complianceType === 'camp_daily') {
        const comp = await formsService.getCampDailyCompliance(teamId, selectedCampId, selectedCampDate)
        setCampComplianceList(comp)
      } else if (staffTab === 'inbox' && isMedic && inboxType === 'camp_daily') {
        const inbox = await formsService.getCampDailyMedicalInbox(teamId, selectedCampId, selectedCampDate)
        setCampMedicalInboxList(inbox)
      } else if (staffTab === 'trends' && isMedic && trendType === 'camp_daily') {
        const tr = await formsService.getCampDailyTrends(teamId, selectedCampId)
        setCampTrendsList(tr)
      }
    } catch (err) {
      console.error('Error al cargar datos staff camp:', err)
    } finally {
      setIsLoadingCampStaff(false)
    }
  }, [isCoach, isMedic, staffTab, complianceType, inboxType, trendType, teamId, selectedCampId, selectedCampDate])

  useEffect(() => {
    loadCampStaffData()
  }, [loadCampStaffData])

  const handleOpenCampDailyForm = async (obl: FormObligation) => {
    if (!athleteId) return
    setActiveDailyObligation(obl)
    try {
      const resp = await formsService.getCampDailyCheckin({
        teamId,
        athleteId,
        eventId: obl.event_id || selectedCampId,
        targetDate: obl.period_start || selectedCampDate,
      })
      setActiveDailyResponse(resp)
    } catch {
      setActiveDailyResponse(null)
    }
    setCampDailyModalOpen(true)
  }

  const handleContactStatusUpdate = async (reqId: string, status: 'in_progress' | 'resolved') => {
    try {
      await formsService.updateContactRequest(
        reqId,
        status,
        status === 'resolved' ? 'Atendido y resuelto por el sanitario.' : 'En seguimiento médico.'
      )
      loadData()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      alert(`Error al actualizar solicitud: ${msg}`)
    }
  }

  return (
    <div>
      {/* Toast de notificación */}
      {notificationMsg && (
        <div
          style={{
            padding: '0.85rem 1rem',
            backgroundColor: 'var(--status-success-bg)',
            color: 'var(--status-success-text)',
            borderRadius: '8px',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.88rem',
            boxShadow: 'var(--shadow-card)',
          }}
        >
          <CheckCircle2 size={18} />
          <span>{notificationMsg}</span>
        </div>
      )}

      {/* Cabecera del Módulo */}
      <div style={{ marginBottom: '1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <h1 style={{ fontSize: '1.5rem', marginBottom: '0.2rem' }}>Formularios y Salud</h1>
            {isLoading && (
              <span className="badge badge-accent" style={{ fontSize: '0.72rem' }}>
                Actualizando...
              </span>
            )}
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
            {isAthlete
              ? 'Seguimiento de bienestar, carga de entrenamiento sRPE e incidencias bajo demanda.'
              : isMedic
              ? 'Bandeja sanitaria, solicitudes de contacto y publicación de disponibilidad operativa.'
              : 'Cumplimiento de formularios del equipo, carga deportiva autorizada y restricciones operativas.'}
          </p>
        </div>

        {/* Acciones directas según rol */}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {isAthlete && (
            <>
              <button
                type="button"
                onClick={() => {
                  setSelectedTrainingLog(null)
                  setTrainingModalOpen(true)
                }}
                className="btn btn-secondary"
                style={{ fontSize: '0.78rem' }}
              >
                <Dumbbell size={15} style={{ marginRight: '0.3rem' }} />
                Registrar entrenamiento
              </button>
              <button
                type="button"
                onClick={() => setIncidentModalOpen(true)}
                className="btn btn-secondary"
                style={{ fontSize: '0.78rem' }}
              >
                <Activity size={15} style={{ marginRight: '0.3rem', color: 'var(--brand-red)' }} />
                Comunicar molestia
              </button>
            </>
          )}

          {(isCoach || isMedic) && (
            <button
              type="button"
              onClick={() => setScheduleModalOpen(true)}
              className="btn btn-secondary"
              style={{ fontSize: '0.78rem' }}
            >
              <Settings size={15} style={{ marginRight: '0.3rem' }} />
              Configurar horario semanal
            </button>
          )}

          {isMedic && (
            <button
              type="button"
              onClick={() => {
                setSelectedAssessmentAthlete({
                  id: 'a1111111-0000-0000-0000-000000000001',
                  name: 'Alejandro García',
                  dorsal: 7,
                  assessment: assessments.find((a) => a.athlete_id === 'a1111111-0000-0000-0000-000000000001'),
                })
                setMedicModalOpen(true)
              }}
              className="btn btn-primary"
              style={{ fontSize: '0.78rem' }}
            >
              <ShieldAlert size={15} style={{ marginRight: '0.3rem' }} />
              Publicar disponibilidad operativa
            </button>
          )}
        </div>
      </div>

      {/* Navegación por pestañas adaptativa y accesible en móvil (M6) */}
      <div className="health-nav-tabs">
        {isAthlete ? (
          <>
            <button
              type="button"
              onClick={() => setAthleteTab('obligations')}
              className={`health-nav-btn ${athleteTab === 'obligations' ? 'btn-primary' : 'btn-secondary'}`}
            >
              <ClipboardList size={14} style={{ marginRight: '0.25rem' }} />
              Mis Formularios
              {obligations && obligations.length > 0 && (
                <span
                  style={{
                    marginLeft: '0.35rem',
                    backgroundColor: obligations.some((o) => o.status === 'overdue')
                      ? 'var(--brand-red)'
                      : 'var(--brand-yellow)',
                    color: '#ffffff',
                    borderRadius: '10px',
                    padding: '0.1rem 0.45rem',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                  }}
                >
                  {obligations.length}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => setAthleteTab('training')}
              className={`health-nav-btn ${athleteTab === 'training' ? 'btn-primary' : 'btn-secondary'}`}
            >
              <Dumbbell size={14} style={{ marginRight: '0.25rem' }} />
              Mis Entrenamientos ({trainingLogs?.length || 0})
            </button>
            <button
              type="button"
              onClick={() => setAthleteTab('incidents')}
              className={`health-nav-btn ${athleteTab === 'incidents' ? 'btn-primary' : 'btn-secondary'}`}
            >
              <Activity size={14} style={{ marginRight: '0.25rem' }} />
              Molestias e Incidencias ({incidents?.length || 0})
            </button>
            <button
              type="button"
              onClick={() => setAthleteTab('profile')}
              className={`health-nav-btn ${athleteTab === 'profile' ? 'btn-primary' : 'btn-secondary'}`}
            >
              <UserCheck size={14} style={{ marginRight: '0.25rem' }} />
              Mi Perfil Deportivo
            </button>
          </>
        ) : (
          <>
            {isMedic && (
              <button
                type="button"
                onClick={() => setStaffTab('inbox')}
                className={`health-nav-btn ${staffTab === 'inbox' ? 'btn-primary' : 'btn-secondary'}`}
              >
                <HeartPulse size={14} style={{ marginRight: '0.25rem', color: staffTab === 'inbox' ? '#fff' : 'var(--brand-red)' }} />
                Bandeja Sanitaria
                {contactRequests && contactRequests.filter((r) => r.status === 'pending').length > 0 && (
                  <span
                    style={{
                      marginLeft: '0.35rem',
                      backgroundColor: 'var(--brand-red)',
                      color: '#ffffff',
                      borderRadius: '10px',
                      padding: '0.1rem 0.45rem',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                    }}
                  >
                    {contactRequests.filter((r) => r.status === 'pending').length}
                  </span>
                )}
              </button>
            )}
            <button
              type="button"
              onClick={() => setStaffTab('compliance')}
              className={`health-nav-btn ${staffTab === 'compliance' ? 'btn-primary' : 'btn-secondary'}`}
            >
              <ClipboardList size={14} style={{ marginRight: '0.25rem' }} />
              Cumplimiento del Equipo
            </button>
            <button
              type="button"
              onClick={() => setStaffTab('workload')}
              className={`health-nav-btn ${staffTab === 'workload' ? 'btn-primary' : 'btn-secondary'}`}
            >
              <TrendingUp size={14} style={{ marginRight: '0.25rem' }} />
              Carga Deportiva (sRPE)
            </button>
            <button
              type="button"
              onClick={() => setStaffTab('availability')}
              className={`health-nav-btn ${!isMedic ? 'span-full ' : ''}${staffTab === 'availability' ? 'btn-primary' : 'btn-secondary'}`}
            >
              <ShieldAlert size={14} style={{ marginRight: '0.25rem' }} />
              Disponibilidad Operativa
            </button>
            {isMedic && (
              <button
                type="button"
                onClick={() => setStaffTab('trends')}
                className={`health-nav-btn span-full ${staffTab === 'trends' ? 'btn-primary' : 'btn-secondary'}`}
              >
                <Activity size={14} style={{ marginRight: '0.25rem' }} />
                Evolución y Bienestar (8 sem)
              </button>
            )}
          </>
        )}
      </div>

      {/* ==================================================================== */}
      {/* SECCIÓN ATLETA */}
      {/* ==================================================================== */}
      {isAthlete && (
        <div>
          {/* PESTAÑA: MIS FORMULARIOS */}
          {athleteTab === 'obligations' && (
            <div>
              {/* Tarjeta de Disponibilidad actual del Atleta */}
              <div
                className="card"
                style={{
                  marginBottom: '1.25rem',
                  padding: '1rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '0.75rem',
                  borderLeft: currentAssessment?.status === 'available'
                    ? '4px solid var(--status-success-text)'
                    : currentAssessment?.status === 'limited'
                    ? '4px solid var(--brand-yellow)'
                    : currentAssessment?.status === 'unavailable'
                    ? '4px solid var(--brand-red)'
                    : '4px solid var(--text-secondary)',
                }}
              >
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    Valoración operativa deportiva actual (emitida por médico):
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.2rem' }}>
                    <span
                      className={`badge ${
                        currentAssessment?.status === 'available'
                          ? 'badge-success'
                          : currentAssessment?.status === 'limited'
                          ? 'badge-warning'
                          : currentAssessment?.status === 'unavailable'
                          ? 'badge-red'
                          : 'badge-muted'
                      }`}
                      style={{ fontSize: '0.85rem', padding: '0.25rem 0.6rem' }}
                    >
                      {currentAssessment?.status === 'available'
                        ? 'Apto sin restricciones'
                        : currentAssessment?.status === 'limited'
                        ? 'Con restricciones'
                        : currentAssessment?.status === 'needs_assessment'
                        ? 'Pendiente de valoración'
                        : currentAssessment?.status === 'unavailable'
                        ? 'No apto temporalmente'
                        : 'Sin valorar'}
                    </span>
                    {currentAssessment?.operational_restriction && (
                      <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                        {currentAssessment.operational_restriction}
                      </span>
                    )}
                  </div>
                </div>

                {currentAssessment?.shared_instructions && (
                  <div
                    style={{
                      fontSize: '0.78rem',
                      backgroundColor: 'var(--surface-sunken)',
                      padding: '0.4rem 0.6rem',
                      borderRadius: '6px',
                      maxWidth: '380px',
                    }}
                  >
                    <strong>Pautas compartidas:</strong> {currentAssessment.shared_instructions}
                  </div>
                )}
              </div>

              {/* LISTA DE PENDIENTES */}
              <div className="table-container" style={{ marginBottom: '1.5rem' }}>
                <div style={{ padding: '0.85rem 1rem', borderBottom: '1px solid var(--surface-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Clock size={16} style={{ color: 'var(--brand-red)' }} />
                    <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: 0 }}>
                      Formularios Pendientes de Entrega
                    </h3>
                  </div>
                  <span className="badge badge-accent">
                    {obligations.length} {obligations.length === 1 ? 'pendiente' : 'pendientes'}
                  </span>
                </div>

                {obligations.length === 0 ? (
                  <div style={{ padding: '2rem', textAlign: 'center' }}>
                    <CheckCircle2 size={32} style={{ color: 'var(--status-success-text)', margin: '0 auto 0.5rem' }} />
                    <h4 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.2rem' }}>
                      ¡Estás al día con tus formularios!
                    </h4>
                    <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: 0 }}>
                      No tienes cuestionarios pendientes de responder en este momento.
                    </p>
                  </div>
                ) : (
                  <div style={{ padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                    {obligations.map((obl, idx) => (
                      <div
                        key={idx}
                        style={{
                          padding: '0.85rem 1rem',
                          backgroundColor: 'var(--surface-sunken)',
                          borderRadius: '8px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: '0.75rem',
                          border: obl.status === 'overdue'
                            ? '1px solid var(--brand-red)'
                            : obl.status === 'draft'
                            ? '1px solid var(--brand-blue, #3b82f6)'
                            : '1px solid var(--surface-border)',
                        }}
                      >
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.2rem' }}>
                            <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>{obl.title}</span>
                            <span
                              className={`badge ${
                                obl.status === 'overdue'
                                  ? 'badge-red'
                                  : obl.status === 'draft'
                                  ? 'badge-accent'
                                  : 'badge-warning'
                              }`}
                            >
                              {obl.status === 'overdue'
                                ? 'Fuera de plazo'
                                : obl.status === 'draft'
                                ? 'Borrador guardado'
                                : 'Pendiente'}
                            </span>
                          </div>

                          <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                            {obl.period_start && obl.period_end ? (
                              <span>
                                Periodo: <strong>{obl.period_start} al {obl.period_end}</strong> ·
                              </span>
                            ) : null}
                            {obl.due_at ? (
                              <span style={{ marginLeft: '0.3rem' }}>
                                Entrega recomendada: {new Date(obl.due_at).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            ) : (
                              <span style={{ marginLeft: '0.3rem' }}>{obl.description}</span>
                            )}
                          </div>
                        </div>

                        <div>
                          {obl.form_type === 'profile' ? (
                            <button
                              type="button"
                              onClick={() => setProfileModalOpen(true)}
                              className="btn btn-primary"
                              style={{ fontSize: '0.78rem' }}
                            >
                              Completar perfil
                            </button>
                          ) : obl.form_type === 'camp_daily' ? (
                            <button
                              type="button"
                              onClick={() => handleOpenCampDailyForm(obl)}
                              className="btn btn-primary"
                              style={{ fontSize: '0.78rem' }}
                            >
                              {obl.status === 'draft' ? 'Continuar borrador' : 'Completar check-in'}
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleOpenWeeklyForm(obl)}
                              className="btn btn-primary"
                              style={{ fontSize: '0.78rem' }}
                            >
                              {obl.status === 'draft' ? 'Continuar borrador' : 'Completar formulario'}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* HISTÓRICO DE RESPUESTAS ANTERIORES */}
              <div className="table-container">
                <div style={{ padding: '0.85rem 1rem', borderBottom: '1px solid var(--surface-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: 0 }}>
                    Historial de Respuestas Anteriores
                  </h3>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    Tus respuestas de bienestar guardadas
                  </span>
                </div>

                {formResponses.length === 0 ? (
                  <div style={{ padding: '1.5rem', textAlign: 'center', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                    No hay respuestas históricas registradas.
                  </div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Periodo</th>
                        <th>Estado</th>
                        <th>Fecha de Envío</th>
                        <th className="numeric">Sueño</th>
                        <th className="numeric">Recuperación</th>
                        <th>Alimentación</th>
                        <th>Disponibilidad</th>
                        <th style={{ width: '100px' }}>Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formResponses.map((r) => {
                        const wp = r.payload as WeeklyFormPayload | undefined
                        return (
                        <tr key={r.id}>
                          <td style={{ fontWeight: 600 }}>
                            {r.period_start} al {r.period_end}
                          </td>
                          <td>
                            <span className={`badge ${r.status === 'submitted' ? (r.is_late ? 'badge-warning' : 'badge-success') : 'badge-accent'}`}>
                              {r.status === 'submitted' ? (r.is_late ? 'Enviado tarde' : 'Enviado') : 'Borrador'}
                            </span>
                          </td>
                          <td style={{ color: 'var(--text-secondary)', fontSize: '0.78rem' }}>
                            {r.submitted_at ? new Date(r.submitted_at).toLocaleString('es-ES') : '—'}
                          </td>
                          <td className="numeric" style={{ fontFamily: 'monospace' }}>
                            {wp?.sleep_hours ? `${wp.sleep_hours}h` : '—'}
                          </td>
                          <td className="numeric" style={{ fontFamily: 'monospace' }}>
                            {wp?.recovery ? `${wp.recovery}/5` : '—'}
                          </td>
                          <td>
                            {wp?.nutrition_quality ? (
                              <span style={{ fontSize: '0.78rem' }}>
                                {NUTRITION_QUALITY_LABELS[wp.nutrition_quality]}
                              </span>
                            ) : (
                              <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem', fontStyle: 'italic' }}>
                                No recogido en esta versión
                              </span>
                            )}
                          </td>
                          <td>
                            <span style={{ fontSize: '0.75rem' }}>
                              {wp?.declared_availability === 'available_100'
                                ? '100%'
                                : wp?.declared_availability === 'limited'
                                ? 'Limitada'
                                : wp?.declared_availability === 'needs_assessment'
                                ? 'Valoración'
                                : 'No disp.'}
                            </span>
                          </td>
                          <td>
                            <button
                              type="button"
                              onClick={() => {
                                setActiveWeeklyObligation({
                                  form_type: 'weekly',
                                  title: 'Seguimiento semanal',
                                  description: '',
                                  priority: 2,
                                  status: r.status,
                                  period_start: r.period_start,
                                  period_end: r.period_end,
                                  open_at: null,
                                  due_at: null,
                                  is_late: r.is_late,
                                  can_submit: true,
                                  response_id: r.id,
                                })
                                setWeeklyModalOpen(true)
                              }}
                              className="btn btn-secondary"
                              style={{ fontSize: '0.7rem', padding: '0.2rem 0.45rem' }}
                            >
                              Ver / Editar
                            </button>
                          </td>
                        </tr>
                        )
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* PESTAÑA: MIS ENTRENAMIENTOS */}
          {athleteTab === 'training' && (
            <div>
              {/* Tarjetas resumen de carga sRPE */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
                <div className="card" style={{ padding: '0.85rem' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Sesiones Registradas</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800 }}>{trainingLogs.length}</div>
                </div>
                <div className="card" style={{ padding: '0.85rem' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Minutos Totales</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800 }}>
                    {trainingLogs.reduce((acc, l) => acc + l.duration_minutes, 0)} min
                  </div>
                </div>
                <div className="card" style={{ padding: '0.85rem' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Carga sRPE Acumulada</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--brand-red)' }}>
                    {Math.round(trainingLogs.reduce((acc, l) => acc + Number(l.session_load || l.duration_minutes * l.rpe), 0))} UA
                  </div>
                </div>
              </div>

              {/* Resumen Semanal de Carga */}
              {athleteWeeklyAggregates.length > 0 && (
                <div className="table-container" style={{ marginBottom: '1.25rem' }}>
                  <div style={{ padding: '0.65rem 1rem', borderBottom: '1px solid var(--surface-border)', fontSize: '0.85rem', fontWeight: 700 }}>
                    Resumen Semanal de Carga (sRPE)
                  </div>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Semana</th>
                        <th className="numeric">Sesiones</th>
                        <th className="numeric">Minutos</th>
                        <th className="numeric">Carga sRPE</th>
                      </tr>
                    </thead>
                    <tbody>
                      {athleteWeeklyAggregates.slice(0, 5).map((agg) => (
                        <tr key={agg.week_start}>
                          <td style={{ fontWeight: 600 }}>{agg.week_start} al {agg.week_end}</td>
                          <td className="numeric">{agg.session_count}</td>
                          <td className="numeric">{agg.total_minutes} min</td>
                          <td className="numeric" style={{ fontWeight: 700, color: 'var(--brand-red)' }}>
                            {Math.round(agg.total_load)} UA
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Tabla de entrenamientos */}
              <div className="table-container">
                <div style={{ padding: '0.85rem 1rem', borderBottom: '1px solid var(--surface-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: 0 }}>
                    Historial de Sesiones Realizadas
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedTrainingLog(null)
                      setTrainingModalOpen(true)
                    }}
                    className="btn btn-primary"
                    style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                  >
                    <Plus size={14} style={{ marginRight: '0.2rem' }} />
                    Nueva sesión
                  </button>
                </div>

                {trainingLogs.length === 0 ? (
                  <div style={{ padding: '1.5rem', textAlign: 'center', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                    No has registrado sesiones de entrenamiento todavía.
                  </div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Actividad</th>
                        <th>Origen</th>
                        <th className="numeric">Duración</th>
                        <th className="numeric">RPE</th>
                        <th className="numeric">Carga sRPE</th>
                        <th>Comentario</th>
                        <th style={{ width: '80px' }}>Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trainingLogs.map((log) => (
                        <tr key={log.id}>
                          <td style={{ fontWeight: 600 }}>{log.session_date}</td>
                          <td>
                            <span style={{ textTransform: 'capitalize' }}>{log.activity_type}</span>
                            {log.other_activity_description && ` (${log.other_activity_description})`}
                          </td>
                          <td>
                            <span className="badge badge-muted">
                              {log.origin === 'selection' ? 'Selección' : log.origin === 'club' ? 'Club' : 'Propio'}
                            </span>
                          </td>
                          <td className="numeric" style={{ fontFamily: 'monospace' }}>
                            {log.duration_minutes}m
                          </td>
                          <td className="numeric" style={{ fontFamily: 'monospace', fontWeight: 700 }}>
                            {log.rpe}
                          </td>
                          <td className="numeric" style={{ fontFamily: 'monospace', fontWeight: 800, color: 'var(--brand-red)' }}>
                            {Math.round(log.session_load || log.duration_minutes * log.rpe)} UA
                          </td>
                          <td style={{ color: 'var(--text-secondary)', fontSize: '0.78rem', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {log.sports_comment || '—'}
                          </td>
                          <td>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedTrainingLog(log)
                                setTrainingModalOpen(true)
                              }}
                              className="btn btn-secondary"
                              style={{ fontSize: '0.7rem', padding: '0.2rem 0.45rem' }}
                            >
                              Editar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* PESTAÑA: MOLESTIAS E INCIDENCIAS */}
          {athleteTab === 'incidents' && (
            <div>
              <div className="table-container">
                <div style={{ padding: '0.85rem 1rem', borderBottom: '1px solid var(--surface-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: 0 }}>
                      Molestias y Episodios Comunicados
                    </h3>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      Tus comunicaciones con el servicio médico
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIncidentModalOpen(true)}
                    className="btn btn-primary"
                    style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                  >
                    <Plus size={14} style={{ marginRight: '0.2rem' }} />
                    Comunicar molestia
                  </button>
                </div>

                {incidents.length === 0 ? (
                  <div style={{ padding: '2rem', textAlign: 'center' }}>
                    <CheckCircle2 size={32} style={{ color: 'var(--status-success-text)', margin: '0 auto 0.5rem' }} />
                    <h4 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.2rem' }}>
                      Sin molestias registradas
                    </h4>
                    <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      No tienes incidencias médicas ni molestias abiertas.
                    </p>
                  </div>
                ) : (
                  <div style={{ padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                    {incidents.map((inc) => (
                      <div
                        key={inc.id}
                        style={{
                          padding: '0.85rem 1rem',
                          backgroundColor: 'var(--surface-sunken)',
                          borderRadius: '8px',
                          border: '1px solid var(--surface-border)',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.4rem' }}>
                          <div>
                            <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>
                              {inc.body_zone} ({inc.laterality || 'N/A'})
                            </span>
                            <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginLeft: '0.5rem' }}>
                              Fecha: {inc.incident_date}
                            </span>
                          </div>
                          <span className={`badge ${inc.status === 'open' ? 'badge-red' : inc.status === 'monitoring' ? 'badge-warning' : 'badge-success'}`}>
                            {inc.status === 'open' ? 'Abierta' : inc.status === 'monitoring' ? 'En seguimiento' : 'Resuelta'}
                          </span>
                        </div>

                        <p style={{ fontSize: '0.82rem', margin: '0.2rem 0 0.5rem', lineHeight: 1.4 }}>
                          {inc.description}
                        </p>

                        <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: 'var(--text-secondary)', flexWrap: 'wrap' }}>
                          <span>Dolor: <strong>{inc.pain_level !== null ? `${inc.pain_level}/10` : '—'}</strong></span>
                          <span>Efecto: <strong>{inc.activity_effect || '—'}</strong></span>
                          <span>Disponibilidad declarada: <strong>{inc.declared_availability || '—'}</strong></span>
                        </div>

                        {/* Pautas compartidas por el médico */}
                        {inc.shared_notes && (
                          <div
                            style={{
                              marginTop: '0.6rem',
                              padding: '0.5rem 0.75rem',
                              backgroundColor: 'rgba(59, 130, 246, 0.08)',
                              borderRadius: '6px',
                              borderLeft: '3px solid var(--brand-blue, #3b82f6)',
                              fontSize: '0.78rem',
                            }}
                          >
                            <strong>Indicaciones del médico / fisio:</strong> {inc.shared_notes}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* PESTAÑA: MI PERFIL */}
          {athleteTab === 'profile' && athleteProfile && (
            <div className="card" style={{ maxWidth: '640px', padding: '1.25rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <div>
                  <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>
                    {athleteProfile.first_name} {athleteProfile.last_name}
                  </h3>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                    Dorsal #{athleteProfile.dorsal_number ?? '—'} · Posición: {athleteProfile.primary_position}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setProfileModalOpen(true)}
                  className="btn btn-secondary"
                  style={{ fontSize: '0.75rem' }}
                >
                  Editar perfil
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem', fontSize: '0.82rem' }}>
                <div>
                  <span style={{ color: 'var(--text-secondary)' }}>Fecha de nacimiento:</span>
                  <div style={{ fontWeight: 600 }}>{athleteProfile.birth_date || 'No completada'}</div>
                </div>
                <div>
                  <span style={{ color: 'var(--text-secondary)' }}>Modalidad:</span>
                  <div style={{ fontWeight: 600, textTransform: 'capitalize' }}>{athleteProfile.modality || 'Flag'}</div>
                </div>
                <div>
                  <span style={{ color: 'var(--text-secondary)' }}>Club Flag:</span>
                  <div style={{ fontWeight: 600 }}>{athleteProfile.flag_club || '—'}</div>
                </div>
                <div>
                  <span style={{ color: 'var(--text-secondary)' }}>Club Tackle:</span>
                  <div style={{ fontWeight: 600 }}>{athleteProfile.tackle_club || '—'}</div>
                </div>
                <div>
                  <span style={{ color: 'var(--text-secondary)' }}>Año de inicio:</span>
                  <div style={{ fontWeight: 600 }}>{athleteProfile.start_year || '—'}</div>
                </div>
                <div>
                  <span style={{ color: 'var(--text-secondary)' }}>Ciudad:</span>
                  <div style={{ fontWeight: 600 }}>{athleteProfile.city || '—'} ({athleteProfile.country})</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ==================================================================== */}
      {/* SECCIÓN STAFF: COACH Y SANITARIO */}
      {/* ==================================================================== */}
      {(isCoach || isMedic) && (
        <div>
          {/* PESTAÑA: BANDEJA SANITARIA (Solo Sanitarios) */}
          {staffTab === 'inbox' && isMedic && (
            <div>
              {/* Selector de sub-bandeja sanitaria: General M6 vs Concentración M8D */}
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', borderBottom: '1px solid var(--surface-border)', paddingBottom: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => setInboxType('camp_daily')}
                  className={`btn ${inboxType === 'camp_daily' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem' }}
                >
                  Check-in Diario Concentración (M8D)
                </button>
                <button
                  type="button"
                  onClick={() => setInboxType('general')}
                  className={`btn ${inboxType === 'general' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem' }}
                >
                  Bandeja General & Episodios (M6)
                </button>
              </div>

              {inboxType === 'camp_daily' ? (
                <>
                  {/* Selectores de concentración y jornada */}
                  <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Concentración:</span>
                      <select
                        value={selectedCampId}
                        onChange={(e) => setSelectedCampId(e.target.value)}
                        className="input"
                        style={{ fontSize: '0.82rem', padding: '0.35rem 0.6rem' }}
                      >
                        <option value="e0000000-0000-0000-0000-000000000001">
                          Concentración Nacional Flag 5v5 — Preparatoria
                        </option>
                      </select>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Jornada:</span>
                      <div style={{ display: 'flex', gap: '0.3rem' }}>
                        {[
                          { date: '2026-05-15', label: 'Viernes 15 may (J1)' },
                          { date: '2026-05-16', label: 'Sábado 16 may (J2)' },
                          { date: '2026-05-17', label: 'Domingo 17 may (J3)' },
                        ].map((d) => (
                          <button
                            key={d.date}
                            type="button"
                            onClick={() => setSelectedCampDate(d.date)}
                            className={`btn ${selectedCampDate === d.date ? 'btn-primary' : 'btn-secondary'}`}
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.55rem' }}
                          >
                            {d.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                      <span className="badge badge-accent">
                        Entregados: {campMedicalInboxList.filter((c) => c.compliance_status === 'submitted' || c.compliance_status === 'late_submitted').length} / {campMedicalInboxList.length}
                      </span>
                      <span className="badge badge-red">
                        Contactos solicitados: {campMedicalInboxList.filter((c) => c.contact_requested).length}
                      </span>
                      <span className="badge badge-warning">
                        Con molestias: {campMedicalInboxList.filter((c) => c.discomfort_status === 'new' || c.discomfort_status === 'worsened').length}
                      </span>
                    </div>
                  </div>

                  {/* Tabla médica de check-in diario */}
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th style={{ width: '50px' }}>Dorsal</th>
                          <th>Jugador</th>
                          <th>Estado</th>
                          <th>Sueño</th>
                          <th>Fatiga</th>
                          <th>Recuperación</th>
                          <th>Molestia</th>
                          <th>Contacto Sanitario</th>
                          <th style={{ width: '100px' }}>Acción médica</th>
                        </tr>
                      </thead>
                      <tbody>
                        {campMedicalInboxList.length === 0 ? (
                          <tr>
                            <td colSpan={9} style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text-secondary)' }}>
                              {isLoadingCampStaff ? 'Cargando bandeja de la jornada...' : 'No hay datos médicos de check-in para esta jornada.'}
                            </td>
                          </tr>
                        ) : (
                          campMedicalInboxList.map((item) => (
                            <tr key={item.athlete_id}>
                              <td style={{ fontWeight: 700, fontFamily: 'monospace' }}>
                                {item.dorsal_number !== null ? `#${item.dorsal_number}` : '—'}
                              </td>
                              <td>
                                <div style={{ fontWeight: 600 }}>{item.first_name} {item.last_name}</div>
                                <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{item.primary_position}</div>
                              </td>
                              <td>
                                <span
                                  className={`badge ${
                                    item.compliance_status === 'submitted'
                                      ? 'badge-success'
                                      : item.compliance_status === 'late_submitted'
                                      ? 'badge-warning'
                                      : item.compliance_status === 'draft'
                                      ? 'badge-accent'
                                      : 'badge-red'
                                  }`}
                                >
                                  {item.compliance_status === 'submitted'
                                    ? 'A tiempo'
                                    : item.compliance_status === 'late_submitted'
                                    ? 'Tardío (> 09:00)'
                                    : item.compliance_status === 'draft'
                                    ? 'Borrador'
                                    : 'Pendiente'}
                                </span>
                                {item.submitted_at && (
                                  <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', marginTop: '0.15rem' }}>
                                    {new Date(item.submitted_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                                  </div>
                                )}
                              </td>
                              <td>
                                {item.sleep_hours !== null ? (
                                  <div>
                                    <div style={{ fontWeight: 700 }}>{item.sleep_hours}h</div>
                                    <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                                      {item.sleep_quality ? SLEEP_QUALITY_LABELS[item.sleep_quality as keyof typeof SLEEP_QUALITY_LABELS] : ''}
                                    </div>
                                  </div>
                                ) : (
                                  <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>—</span>
                                )}
                              </td>
                              <td>
                                {typeof item.fatigue_level === 'number' ? (
                                  <div>
                                    <span
                                      className={`badge ${
                                        item.fatigue_level <= 2
                                          ? 'badge-success'
                                          : item.fatigue_level === 3
                                          ? 'badge-warning'
                                          : 'badge-red'
                                      }`}
                                    >
                                      {item.fatigue_level}/5 · {FATIGUE_LABELS[item.fatigue_level as keyof typeof FATIGUE_LABELS]}
                                    </span>
                                  </div>
                                ) : typeof item.fatigue === 'number' ? (
                                  <div>
                                    <span
                                      className={`badge ${
                                        item.fatigue <= 2
                                          ? 'badge-success'
                                          : item.fatigue === 3
                                          ? 'badge-warning'
                                          : 'badge-red'
                                      }`}
                                    >
                                      {item.fatigue}/5 · {FATIGUE_LABELS[item.fatigue as keyof typeof FATIGUE_LABELS]}
                                    </span>
                                  </div>
                                ) : (
                                  <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>—</span>
                                )}
                              </td>
                              <td>
                                {typeof item.recovery_level === 'number' ? (
                                  <div>
                                    <span
                                      className={`badge ${
                                        item.recovery_level >= 4
                                          ? 'badge-success'
                                          : item.recovery_level === 3
                                          ? 'badge-warning'
                                          : 'badge-red'
                                      }`}
                                    >
                                      {item.recovery_level}/5 · {RECOVERY_LABELS[item.recovery_level as keyof typeof RECOVERY_LABELS]}
                                    </span>
                                  </div>
                                ) : typeof item.recovery === 'number' ? (
                                  <div>
                                    <span
                                      className={`badge ${
                                        item.recovery >= 4
                                          ? 'badge-success'
                                          : item.recovery === 3
                                          ? 'badge-warning'
                                          : 'badge-red'
                                      }`}
                                    >
                                      {item.recovery}/5 · {RECOVERY_LABELS[item.recovery as keyof typeof RECOVERY_LABELS]}
                                    </span>
                                  </div>
                                ) : (
                                  <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>—</span>
                                )}
                              </td>
                              <td>
                                {item.discomfort_status ? (
                                  <span
                                    className={`badge ${
                                      item.discomfort_status === 'none'
                                        ? 'badge-success'
                                        : item.discomfort_status === 'new'
                                        ? 'badge-red'
                                        : 'badge-warning'
                                    }`}
                                  >
                                    {DISCOMFORT_STATUS_LABELS[item.discomfort_status as keyof typeof DISCOMFORT_STATUS_LABELS]}
                                  </span>
                                ) : (
                                  <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>—</span>
                                )}
                              </td>
                              <td>
                                {item.contact_requested ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                    <span className="badge badge-red" style={{ fontSize: '0.7rem' }}>
                                      Solicita contacto
                                    </span>
                                    {item.contact_comment && (
                                      <div style={{ fontSize: '0.72rem', color: 'var(--text-primary)', fontStyle: 'italic' }}>
                                        "{item.contact_comment}"
                                      </div>
                                    )}
                                    {item.contact_request_id && (
                                      <div style={{ display: 'flex', gap: '0.3rem', marginTop: '0.2rem' }}>
                                        <button
                                          type="button"
                                          onClick={async () => {
                                            await formsService.updateContactRequest(
                                              item.contact_request_id!,
                                              'resolved',
                                              'Atendido durante la jornada de concentración.'
                                            )
                                            loadCampStaffData()
                                          }}
                                          className="btn btn-secondary"
                                          style={{ fontSize: '0.68rem', padding: '0.15rem 0.4rem' }}
                                        >
                                          Resolver
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>No requerida</span>
                                )}
                              </td>
                              <td>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedAssessmentAthlete({
                                      id: item.athlete_id,
                                      name: `${item.first_name} ${item.last_name}`,
                                      dorsal: item.dorsal_number,
                                      assessment: assessments.find((a) => a.athlete_id === item.athlete_id),
                                    })
                                    setMedicModalOpen(true)
                                  }}
                                  className="btn btn-primary"
                                  style={{ fontSize: '0.7rem', padding: '0.2rem 0.45rem' }}
                                >
                                  Valorar disp.
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <>
                  {/* Solicitudes de contacto pendientes */}
                  <div className="table-container" style={{ marginBottom: '1.25rem' }}>
                    <div style={{ padding: '0.85rem 1rem', borderBottom: '1px solid var(--surface-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <MessageSquare size={16} style={{ color: 'var(--brand-red)' }} />
                        <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: 0 }}>
                          Solicitudes de Contacto con Sanitario
                        </h3>
                      </div>
                      <span className="badge badge-accent">
                        {contactRequests.filter((r) => r.status === 'pending').length} pendientes
                      </span>
                    </div>

                    {contactRequests.length === 0 ? (
                      <div style={{ padding: '1.5rem', textAlign: 'center', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                        No hay solicitudes de contacto pendientes.
                      </div>
                    ) : (
                      <div style={{ padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {contactRequests.map((req) => (
                          <div
                            key={req.id}
                            style={{
                              padding: '0.75rem 1rem',
                              backgroundColor: 'var(--surface-sunken)',
                              borderRadius: '8px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: '0.6rem',
                              borderLeft: req.status === 'pending' ? '4px solid var(--brand-red)' : '4px solid var(--status-success-text)',
                            }}
                          >
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <strong style={{ fontSize: '0.88rem' }}>{req.athlete_name}</strong>
                                <span className={`badge ${req.status === 'pending' ? 'badge-red' : req.status === 'in_progress' ? 'badge-warning' : 'badge-success'}`}>
                                  {req.status === 'pending' ? 'Pendiente' : req.status === 'in_progress' ? 'En curso' : 'Resuelta'}
                                </span>
                              </div>
                              <div style={{ fontSize: '0.8rem', marginTop: '0.2rem' }}>{req.reason}</div>
                              <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>
                                Solicitado el {new Date(req.requested_at).toLocaleString('es-ES')}
                                {req.resolution_notes && ` · Nota: ${req.resolution_notes}`}
                              </div>
                            </div>

                            <div style={{ display: 'flex', gap: '0.4rem' }}>
                              {req.status === 'pending' && (
                                <button
                                  type="button"
                                  onClick={() => handleContactStatusUpdate(req.id, 'in_progress')}
                                  className="btn btn-secondary"
                                  style={{ fontSize: '0.72rem', padding: '0.25rem 0.5rem' }}
                                >
                                  Marcar en curso
                                </button>
                              )}
                              {req.status !== 'resolved' && (
                                <button
                                  type="button"
                                  onClick={() => handleContactStatusUpdate(req.id, 'resolved')}
                                  className="btn btn-primary"
                                  style={{ fontSize: '0.72rem', padding: '0.25rem 0.5rem', backgroundColor: 'var(--status-success-text)' }}
                                >
                                  Marcar resuelta
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Casos clínicos y episodios abiertos */}
                  <div className="table-container">
                    <div style={{ padding: '0.85rem 1rem', borderBottom: '1px solid var(--surface-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <ShieldAlert size={16} style={{ color: 'var(--brand-yellow)' }} />
                        <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: 0 }}>
                          Episodios y Molestias en Seguimiento
                        </h3>
                      </div>
                    </div>

                    <div style={{ padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                      {allIncidents.map((inc) => (
                        <div
                          key={inc.id}
                          style={{
                            padding: '0.85rem 1rem',
                            backgroundColor: 'var(--surface-sunken)',
                            borderRadius: '8px',
                            border: '1px solid var(--surface-border)',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.35rem' }}>
                            <div>
                              <strong style={{ fontSize: '0.9rem' }}>{inc.athlete_name}</strong>
                              <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginLeft: '0.5rem' }}>
                                {inc.body_zone} ({inc.laterality || 'N/A'}) · {inc.incident_date}
                              </span>
                            </div>
                            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                              <span className={`badge ${inc.status === 'open' ? 'badge-red' : inc.status === 'monitoring' ? 'badge-warning' : 'badge-success'}`}>
                                {inc.status === 'open' ? 'Nueva' : inc.status === 'monitoring' ? 'En seguimiento' : 'Cerrada'}
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedAssessmentAthlete({
                                    id: inc.athlete_id,
                                    name: inc.athlete_name || 'Atleta',
                                    incident: inc,
                                  })
                                  setMedicModalOpen(true)
                                }}
                                className="btn btn-secondary"
                                style={{ fontSize: '0.7rem', padding: '0.2rem 0.45rem' }}
                              >
                                Valorar / Notas
                              </button>
                            </div>
                          </div>

                          <p style={{ fontSize: '0.82rem', margin: '0.2rem 0 0.4rem', lineHeight: 1.4 }}>
                            {inc.description}
                          </p>

                          <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                            <span>Dolor: <strong>{inc.pain_level ?? '—'}/10</strong></span>
                            <span>Efecto: <strong>{inc.activity_effect || '—'}</strong></span>
                            <span>Disp. declarada: <strong>{inc.declared_availability || '—'}</strong></span>
                          </div>

                          {inc.shared_notes && (
                            <div style={{ marginTop: '0.4rem', fontSize: '0.78rem', color: 'var(--brand-blue, #3b82f6)' }}>
                              <strong>Pautas compartidas:</strong> {inc.shared_notes}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* PESTAÑA: CUMPLIMIENTO DEL EQUIPO (Visible para Coach y Sanitario) */}
          {staffTab === 'compliance' && (
            <div>
              {/* Selector de tipo de cumplimiento (Semanal vs Check-in Diario M8D) */}
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', borderBottom: '1px solid var(--surface-border)', paddingBottom: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => setComplianceType('weekly')}
                  className={`btn ${complianceType === 'weekly' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem' }}
                >
                  Seguimiento Semanal (M6)
                </button>
                <button
                  type="button"
                  onClick={() => setComplianceType('camp_daily')}
                  className={`btn ${complianceType === 'camp_daily' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem' }}
                >
                  Check-in Diario Concentración (M8D)
                </button>
              </div>

              {complianceType === 'weekly' ? (
                <>
                  {/* Selector de semana / periodo */}
                  <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Seleccionar semana de seguimiento:</span>
                    <select
                      value={compliancePeriod}
                      onChange={(e) => setCompliancePeriod(e.target.value)}
                      className="input"
                      style={{ fontSize: '0.82rem', padding: '0.35rem 0.6rem' }}
                    >
                      <option value="2026-10-12">Semana 12 oct – 18 oct 2026 (Semana pasada)</option>
                      <option value="2026-10-05">Semana 05 oct – 11 oct 2026</option>
                      <option value="2026-09-28">Semana 28 sep – 04 oct 2026</option>
                      <option value="2026-09-21">Semana 21 sep – 27 sep 2026</option>
                    </select>

                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span className="badge badge-success">
                        {teamCompliance.filter((c) => c.compliance_status === 'submitted').length} a tiempo
                      </span>
                      <span className="badge badge-warning">
                        {teamCompliance.filter((c) => c.compliance_status === 'late_submitted').length} fuera de plazo
                      </span>
                      <span className="badge badge-accent">
                        {teamCompliance.filter((c) => c.compliance_status === 'draft').length} borradores
                      </span>
                      <span className="badge badge-red">
                        {teamCompliance.filter((c) => c.compliance_status === 'pending').length} pendientes
                      </span>
                    </div>
                  </div>

                  {/* Tabla de cumplimiento del equipo */}
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th style={{ width: '60px' }}>Dorsal</th>
                          <th>Jugador</th>
                          <th>Posición</th>
                          <th>Unidad</th>
                          <th>Estado de Cumplimentación</th>
                          <th>Fecha de Entrega</th>
                        </tr>
                      </thead>
                      <tbody>
                        {teamCompliance.map((c) => (
                          <tr key={c.athlete_id}>
                            <td style={{ fontWeight: 700, fontFamily: 'monospace' }}>
                              {c.dorsal_number !== null ? `#${c.dorsal_number}` : '—'}
                            </td>
                            <td style={{ fontWeight: 600 }}>
                              {c.first_name} {c.last_name}
                            </td>
                            <td>{c.primary_position}</td>
                            <td style={{ textTransform: 'capitalize', color: 'var(--text-secondary)' }}>
                              {c.unit}
                            </td>
                            <td>
                              <span
                                className={`badge ${
                                  c.compliance_status === 'submitted'
                                    ? 'badge-success'
                                    : c.compliance_status === 'late_submitted'
                                    ? 'badge-warning'
                                    : c.compliance_status === 'draft'
                                    ? 'badge-accent'
                                    : 'badge-red'
                                }`}
                              >
                                {c.compliance_status === 'submitted'
                                  ? 'Enviado a tiempo'
                                  : c.compliance_status === 'late_submitted'
                                  ? 'Enviado fuera de plazo'
                                  : c.compliance_status === 'draft'
                                  ? 'Borrador iniciado'
                                  : 'Pendiente de entrega'}
                              </span>
                            </td>
                            <td style={{ color: 'var(--text-secondary)', fontSize: '0.78rem' }}>
                              {c.submitted_at ? new Date(c.submitted_at).toLocaleString('es-ES') : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <>
                  {/* Vista de Cumplimiento Check-in Diario de Concentración (M8D) */}
                  <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Concentración:</span>
                      <select
                        value={selectedCampId}
                        onChange={(e) => setSelectedCampId(e.target.value)}
                        className="input"
                        style={{ fontSize: '0.82rem', padding: '0.35rem 0.6rem' }}
                      >
                        <option value="e0000000-0000-0000-0000-000000000001">
                          Concentración Nacional Flag 5v5 — Preparatoria
                        </option>
                      </select>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Jornada:</span>
                      <div style={{ display: 'flex', gap: '0.3rem' }}>
                        {[
                          { date: '2026-05-15', label: 'Viernes 15 may (J1)' },
                          { date: '2026-05-16', label: 'Sábado 16 may (J2)' },
                          { date: '2026-05-17', label: 'Domingo 17 may (J3)' },
                        ].map((d) => (
                          <button
                            key={d.date}
                            type="button"
                            onClick={() => setSelectedCampDate(d.date)}
                            className={`btn ${selectedCampDate === d.date ? 'btn-primary' : 'btn-secondary'}`}
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.55rem' }}
                          >
                            {d.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                      <span className="badge badge-success">
                        {campComplianceList.filter((c) => c.compliance_status === 'submitted').length} a tiempo
                      </span>
                      <span className="badge badge-warning">
                        {campComplianceList.filter((c) => c.compliance_status === 'late_submitted').length} fuera de plazo
                      </span>
                      <span className="badge badge-accent">
                        {campComplianceList.filter((c) => c.compliance_status === 'draft').length} borradores
                      </span>
                      <span className="badge badge-red">
                        {campComplianceList.filter((c) => c.compliance_status === 'pending').length} pendientes
                      </span>
                    </div>
                  </div>

                  <div style={{ marginBottom: '0.75rem', fontSize: '0.78rem', color: 'var(--text-secondary)', backgroundColor: 'var(--surface-sunken)', padding: '0.5rem 0.75rem', borderRadius: '6px' }}>
                    <strong>Privacidad y permisos:</strong> Como cuerpo técnico, esta vista muestra el cumplimiento de check-in (quién ha entregado y quién sigue pendiente). Las respuestas de sueño, fatiga, recuperación, molestias y solicitudes médicas son estrictamente privadas del servicio médico según RLS y PERMISSIONS_MATRIX.
                  </div>

                  {/* Tabla de cumplimiento diario */}
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th style={{ width: '60px' }}>Dorsal</th>
                          <th>Jugador</th>
                          <th>Posición</th>
                          <th>Unidad</th>
                          <th>Estado de Check-in</th>
                          <th>Hora de Entrega</th>
                        </tr>
                      </thead>
                      <tbody>
                        {campComplianceList.length === 0 ? (
                          <tr>
                            <td colSpan={6} style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text-secondary)' }}>
                              {isLoadingCampStaff ? 'Cargando cumplimiento de la jornada...' : 'No hay datos de convocatoria activa para esta jornada.'}
                            </td>
                          </tr>
                        ) : (
                          campComplianceList.map((c) => (
                            <tr key={c.athlete_id}>
                              <td style={{ fontWeight: 700, fontFamily: 'monospace' }}>
                                {c.dorsal_number !== null ? `#${c.dorsal_number}` : '—'}
                              </td>
                              <td style={{ fontWeight: 600 }}>
                                {c.first_name} {c.last_name}
                              </td>
                              <td>{c.primary_position}</td>
                              <td style={{ textTransform: 'capitalize', color: 'var(--text-secondary)' }}>
                                {c.unit}
                              </td>
                              <td>
                                <span
                                  className={`badge ${
                                    c.compliance_status === 'submitted'
                                      ? 'badge-success'
                                      : c.compliance_status === 'late_submitted'
                                      ? 'badge-warning'
                                      : c.compliance_status === 'draft'
                                      ? 'badge-accent'
                                      : 'badge-red'
                                  }`}
                                >
                                  {c.compliance_status === 'submitted'
                                    ? 'A tiempo'
                                    : c.compliance_status === 'late_submitted'
                                    ? 'Tardío (> 09:00)'
                                    : c.compliance_status === 'draft'
                                    ? 'Borrador'
                                    : 'Pendiente'}
                                </span>
                              </td>
                              <td style={{ color: 'var(--text-secondary)', fontSize: '0.78rem' }}>
                                {c.submitted_at
                                  ? new Date(c.submitted_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
                                  : '—'}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {/* PESTAÑA: CARGA DEPORTIVA sRPE (Coach y Sanitario) */}
          {staffTab === 'workload' && (
            <div>
              <div className="table-container">
                <div style={{ padding: '0.85rem 1rem', borderBottom: '1px solid var(--surface-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: 0 }}>
                    Carga Deportiva Autorizada del Equipo (sRPE = Duración × RPE)
                  </h3>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    Total sesiones registradas: {teamLogs.length}
                  </span>
                </div>

                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Actividad</th>
                      <th>Origen</th>
                      <th className="numeric">Duración</th>
                      <th className="numeric">RPE</th>
                      <th className="numeric">Carga sRPE</th>
                      <th>Comentario deportivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamLogs.slice(0, 20).map((l) => (
                      <tr key={l.id}>
                        <td style={{ fontWeight: 600 }}>{l.session_date}</td>
                        <td style={{ textTransform: 'capitalize' }}>
                          {l.activity_type}
                          {l.other_activity_description && ` (${l.other_activity_description})`}
                        </td>
                        <td>
                          <span className="badge badge-muted">
                            {l.origin === 'selection' ? 'Selección' : l.origin === 'club' ? 'Club' : 'Propio'}
                          </span>
                        </td>
                        <td className="numeric" style={{ fontFamily: 'monospace' }}>{l.duration_minutes}m</td>
                        <td className="numeric" style={{ fontFamily: 'monospace', fontWeight: 700 }}>{l.rpe}</td>
                        <td className="numeric" style={{ fontFamily: 'monospace', fontWeight: 800, color: 'var(--brand-red)' }}>
                          {Math.round(l.session_load || l.duration_minutes * l.rpe)} UA
                        </td>
                        <td style={{ color: 'var(--text-secondary)', fontSize: '0.78rem' }}>
                          {l.sports_comment || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* PESTAÑA: DISPONIBILIDAD OPERATIVA (Coach y Sanitario) */}
          {staffTab === 'availability' && (
            <div>
              <div className="table-container">
                <div style={{ padding: '0.85rem 1rem', borderBottom: '1px solid var(--surface-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: 0 }}>
                      Disponibilidad Operativa y Restricciones Prácticas de Campo
                    </h3>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      Publicado exclusivamente por el equipo médico para conocimiento del staff
                    </span>
                  </div>
                </div>

                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ width: '60px' }}>Dorsal</th>
                      <th>Jugador</th>
                      <th>Estado Operativo</th>
                      <th>Restricciones Prácticas para Campo</th>
                      <th>Pautas Compartidas</th>
                      <th>Válido desde</th>
                      <th>Próx. Revisión</th>
                      {isMedic && <th style={{ width: '80px' }}>Acción</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {assessments.map((a) => (
                      <tr key={a.id}>
                        <td style={{ fontWeight: 700, fontFamily: 'monospace' }}>
                          {a.dorsal_number !== null ? `#${a.dorsal_number}` : '—'}
                        </td>
                        <td style={{ fontWeight: 600 }}>{a.athlete_name}</td>
                        <td>
                          <span
                            className={`badge ${
                              a.status === 'available'
                                ? 'badge-success'
                                : a.status === 'limited'
                                ? 'badge-warning'
                                : a.status === 'needs_assessment'
                                ? 'badge-accent'
                                : 'badge-red'
                            }`}
                          >
                            {a.status === 'available'
                              ? 'Apto'
                              : a.status === 'limited'
                              ? 'Con restricciones'
                              : a.status === 'needs_assessment'
                              ? 'Pendiente valoración'
                              : 'No apto'}
                          </span>
                        </td>
                        <td style={{ fontSize: '0.78rem', color: a.operational_restriction ? 'var(--brand-red)' : 'var(--text-secondary)' }}>
                          {a.operational_restriction || 'Sin restricciones'}
                        </td>
                        <td style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                          {a.shared_instructions || '—'}
                        </td>
                        <td style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{a.valid_from}</td>
                        <td style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                          {a.next_review_date || '—'}
                        </td>
                        {isMedic && (
                          <td>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedAssessmentAthlete({
                                  id: a.athlete_id,
                                  name: a.athlete_name || 'Atleta',
                                  dorsal: a.dorsal_number,
                                  assessment: a,
                                })
                                setMedicModalOpen(true)
                              }}
                              className="btn btn-secondary"
                              style={{ fontSize: '0.7rem', padding: '0.2rem 0.45rem' }}
                            >
                              Modificar
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* PESTAÑA: EVOLUCIÓN Y BIENESTAR 8 SEMANAS (Solo Sanitario) */}
          {staffTab === 'trends' && isMedic && (
            <div>
              {/* Selector de sub-pestaña de tendencias */}
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', borderBottom: '1px solid var(--surface-border)', paddingBottom: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => setTrendType('camp_daily')}
                  className={`btn ${trendType === 'camp_daily' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem' }}
                >
                  Evolución Diaria Concentración (M8D)
                </button>
                <button
                  type="button"
                  onClick={() => setTrendType('weekly')}
                  className={`btn ${trendType === 'weekly' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem' }}
                >
                  Serie 8 Semanas (M6)
                </button>
              </div>

              {trendType === 'camp_daily' ? (
                <>
                  <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Concentración:</span>
                      <select
                        value={selectedCampId}
                        onChange={(e) => setSelectedCampId(e.target.value)}
                        className="input"
                        style={{ fontSize: '0.82rem', padding: '0.35rem 0.6rem' }}
                      >
                        <option value="e0000000-0000-0000-0000-000000000001">
                          Concentración Nacional Flag 5v5 — Preparatoria
                        </option>
                      </select>
                    </div>
                  </div>

                  {/* Tabla de evolución diaria agregada */}
                  <div className="table-container">
                    <div style={{ padding: '0.85rem 1rem', borderBottom: '1px solid var(--surface-border)' }}>
                      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: 0 }}>
                        Evolución de Bienestar del Equipo por Jornada de Concentración
                      </h3>
                      <span style={{ fontSize: '0.74rem', color: 'var(--text-secondary)' }}>
                        Valores promedio calculados sobre respuestas entregadas. Ausencia de datos se muestra como '—', nunca como cero.
                      </span>
                    </div>

                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Jornada</th>
                          <th className="numeric">Respuestas</th>
                          <th className="numeric">Sueño medio</th>
                          <th className="numeric">Calidad media</th>
                          <th className="numeric">Fatiga media</th>
                          <th className="numeric">Recuperación media</th>
                          <th className="numeric">Molestias</th>
                          <th className="numeric">Solicitudes médico</th>
                        </tr>
                      </thead>
                      <tbody>
                        {campTrendsList.length === 0 ? (
                          <tr>
                            <td colSpan={8} style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text-secondary)' }}>
                              {isLoadingCampStaff ? 'Cargando tendencias de concentración...' : 'No hay datos agregados disponibles.'}
                            </td>
                          </tr>
                        ) : (
                          campTrendsList.map((tr) => {
                            const dateKey = tr.day_date || tr.checkin_date || 'N/D'
                            const totalResp = tr.total_responses ?? tr.submitted_count
                            const discCount = tr.discomfort_count ?? (tr.discomfort_new_count + tr.discomfort_worsened_count)
                            const contactCount = tr.contact_request_count ?? tr.contact_requests_count
                            return (
                              <tr key={dateKey}>
                                <td style={{ fontWeight: 600 }}>
                                  {dateKey !== 'N/D'
                                    ? new Date(dateKey).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })
                                    : '—'}
                                  <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginLeft: '0.3rem' }}>
                                    ({dateKey})
                                  </span>
                                </td>
                                <td className="numeric" style={{ fontFamily: 'monospace' }}>
                                  {totalResp}
                                </td>
                                <td className="numeric" style={{ fontFamily: 'monospace', fontWeight: 700 }}>
                                  {tr.avg_sleep_hours !== null ? `${tr.avg_sleep_hours}h` : '—'}
                                </td>
                                <td className="numeric" style={{ fontFamily: 'monospace' }}>
                                  {tr.avg_sleep_quality !== null ? `${tr.avg_sleep_quality}/5` : '—'}
                                </td>
                                <td className="numeric" style={{ fontFamily: 'monospace' }}>
                                  {tr.avg_fatigue !== null ? (
                                    <span
                                      className={`badge ${
                                        tr.avg_fatigue <= 2.5
                                          ? 'badge-success'
                                          : tr.avg_fatigue <= 3.5
                                          ? 'badge-warning'
                                          : 'badge-red'
                                      }`}
                                    >
                                      {tr.avg_fatigue}/5
                                    </span>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                                <td className="numeric" style={{ fontFamily: 'monospace' }}>
                                  {tr.avg_recovery !== null ? (
                                    <span
                                      className={`badge ${
                                        tr.avg_recovery >= 3.5
                                          ? 'badge-success'
                                          : tr.avg_recovery >= 2.5
                                          ? 'badge-warning'
                                          : 'badge-red'
                                      }`}
                                    >
                                      {tr.avg_recovery}/5
                                    </span>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                                <td className="numeric" style={{ fontFamily: 'monospace' }}>
                                  {discCount > 0 ? (
                                    <span className="badge badge-warning">{discCount}</span>
                                  ) : (
                                    '0'
                                  )}
                                </td>
                                <td className="numeric" style={{ fontFamily: 'monospace' }}>
                                  {contactCount > 0 ? (
                                    <span className="badge badge-red">{contactCount}</span>
                                  ) : (
                                    '0'
                                  )}
                                </td>
                              </tr>
                            )
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Seleccionar jugador para evolución de 8 semanas:</span>
                    <select
                      value={selectedTrendAthleteId}
                      onChange={(e) => setSelectedTrendAthleteId(e.target.value)}
                      className="input"
                      style={{ fontSize: '0.82rem', padding: '0.35rem 0.6rem' }}
                    >
                      <option value="a1111111-0000-0000-0000-000000000001">#7 Alejandro García (QB)</option>
                      <option value="a1111111-0000-0000-0000-000000000019">#27 Raúl Santos (CB)</option>
                      <option value="a1111111-0000-0000-0000-000000000017">#21 Mario Gil (CB)</option>
                    </select>
                  </div>

              {/* Tabla de evolución de 8 semanas */}
              <div className="table-container">
                <div style={{ padding: '0.85rem 1rem', borderBottom: '1px solid var(--surface-border)' }}>
                  <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: 0 }}>
                    Serie de 8 Semanas: Carga sRPE, Sueño y Bienestar
                  </h3>
                  <span style={{ fontSize: '0.74rem', color: 'var(--text-secondary)' }}>
                    Huecos explícitos cuando faltan datos (sin rellenar con ceros artificiales)
                  </span>
                </div>

                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Semana</th>
                      <th className="numeric">Sesiones</th>
                      <th className="numeric">Minutos</th>
                      <th className="numeric">Carga sRPE</th>
                      <th className="numeric">Días Activos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trendWeeklyAggregates.map((w) => (
                      <tr key={w.week_start}>
                        <td style={{ fontWeight: 600 }}>{w.week_start} al {w.week_end}</td>
                        <td className="numeric" style={{ fontFamily: 'monospace' }}>{w.session_count}</td>
                        <td className="numeric" style={{ fontFamily: 'monospace' }}>{w.total_minutes}m</td>
                        <td className="numeric" style={{ fontFamily: 'monospace', fontWeight: 800, color: 'var(--brand-red)' }}>
                          {w.total_load} UA
                        </td>
                        <td className="numeric" style={{ fontFamily: 'monospace' }}>{w.unique_days} días</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Tabla de evolución de alimentación e hidratación (Solo Sanitario) */}
              <div className="table-container" style={{ marginTop: '1.25rem' }}>
                <div style={{ padding: '0.85rem 1rem', borderBottom: '1px solid var(--surface-border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <Utensils size={15} style={{ color: 'var(--brand-red)' }} />
                    <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: 0 }}>
                      Seguimiento Semanal de Alimentación e Hidratación
                    </h3>
                  </div>
                  <span style={{ fontSize: '0.74rem', color: 'var(--text-secondary)' }}>
                    Datos privados de salud protegidos por RLS. Huecos explícitos para semanas sin registrar o versiones previas (weekly_v1).
                  </span>
                </div>

                {trendAthleteResponses.length === 0 ? (
                  <div style={{ padding: '1.5rem', textAlign: 'center', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                    No hay formularios semanales registrados para este jugador.
                  </div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Semana</th>
                        <th>Versión</th>
                        <th>Calidad Alimentación</th>
                        <th>Agua Diaria</th>
                        <th>Proteína en Comidas</th>
                        <th>Fruta y Verdura</th>
                        <th>Post-Entreno</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trendAthleteResponses.map((resp) => {
                        const payload = resp.payload as WeeklyFormPayload | undefined
                        const isV1 = !payload?.nutrition_quality && !payload?.water_intake
                        return (
                          <tr key={resp.id}>
                            <td style={{ fontWeight: 600 }}>{resp.period_start} al {resp.period_end}</td>
                            <td>
                              <span className={`badge ${resp.version_id === 'weekly_v2' ? 'badge-success' : 'badge-muted'}`} style={{ fontSize: '0.7rem' }}>
                                {resp.version_id || (isV1 ? 'weekly_v1' : 'weekly_v2')}
                              </span>
                            </td>
                            <td>
                              {payload?.nutrition_quality ? (
                                <span style={{ fontWeight: 600 }}>{NUTRITION_QUALITY_LABELS[payload.nutrition_quality]}</span>
                              ) : (
                                <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', fontStyle: 'italic' }}>
                                  No recogido en esta versión
                                </span>
                              )}
                            </td>
                            <td>
                              {payload?.water_intake ? (
                                <span>{WATER_INTAKE_LABELS[payload.water_intake]}</span>
                              ) : (
                                <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', fontStyle: 'italic' }}>
                                  No recogido en esta versión
                                </span>
                              )}
                            </td>
                            <td>
                              {payload?.protein_frequency ? (
                                <span>{PROTEIN_FREQUENCY_LABELS[payload.protein_frequency]}</span>
                              ) : (
                                <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', fontStyle: 'italic' }}>
                                  No recogido en esta versión
                                </span>
                              )}
                            </td>
                            <td>
                              {payload?.fruit_veg_servings ? (
                                <span style={{ fontWeight: 600 }}>{FRUIT_VEG_SERVINGS_LABELS[payload.fruit_veg_servings]} raciones</span>
                              ) : (
                                <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', fontStyle: 'italic' }}>
                                  No recogido en esta versión
                                </span>
                              )}
                            </td>
                            <td>
                              {payload?.post_workout_protein_carb ? (
                                <span style={payload.post_workout_protein_carb === 'not_trained' ? { fontStyle: 'italic', color: 'var(--text-secondary)' } : {}}>
                                  {POST_WORKOUT_INTAKE_LABELS[payload.post_workout_protein_carb]}
                                </span>
                              ) : (
                                <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', fontStyle: 'italic' }}>
                                  No recogido en esta versión
                                </span>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
      )}
        </div>
      )}

      {/* ==================================================================== */}
      {/* MODALES INTERACTIVOS */}
      {/* ==================================================================== */}
      {trainingModalOpen && athleteId && (
        <TrainingLogModal
          isOpen={trainingModalOpen}
          onClose={() => setTrainingModalOpen(false)}
          onSuccess={() => {
            loadData()
            setNotificationMsg('Entrenamiento registrado correctamente.')
            setTimeout(() => setNotificationMsg(null), 3000)
          }}
          teamId={teamId}
          athleteId={athleteId}
          initialLog={selectedTrainingLog}
        />
      )}

      {incidentModalOpen && athleteId && (
        <IncidentReportModal
          isOpen={incidentModalOpen}
          onClose={() => setIncidentModalOpen(false)}
          onSuccess={() => {
            loadData()
            setNotificationMsg('Incidencia comunicada correctamente al servicio médico.')
            setTimeout(() => setNotificationMsg(null), 4000)
          }}
          teamId={teamId}
          athleteId={athleteId}
          openEpisodes={incidents.filter((i) => i.status === 'open' || i.status === 'monitoring')}
        />
      )}

      {weeklyModalOpen && athleteId && activeWeeklyObligation && (
        <WeeklyFormModal
          key={`weekly-modal-${activeWeeklyObligation.period_start}-${activeWeeklyObligation.response_id || 'new'}-${activeWeeklyObligation.status}`}
          isOpen={weeklyModalOpen}
          onClose={() => {
            setWeeklyModalOpen(false)
            setActiveWeeklyObligation(null)
          }}
          onSuccess={handleWeeklySuccess}
          teamId={teamId}
          athleteId={athleteId}
          periodStart={activeWeeklyObligation.period_start || '2026-10-12'}
          periodEnd={activeWeeklyObligation.period_end || '2026-10-18'}
          initialPayload={
            (formResponses.find(
              (r) => r.period_start === activeWeeklyObligation.period_start
            )?.payload as WeeklyFormPayload) || null
          }
          versionId={
            formResponses.find(
              (r) => r.period_start === activeWeeklyObligation.period_start
            )?.version_id || undefined
          }
          openEpisodes={incidents.filter((i) => i.status === 'open' || i.status === 'monitoring')}
          onOpenTrainingModal={() => {
            setWeeklyModalOpen(false)
            setSelectedTrainingLog(null)
            setTrainingModalOpen(true)
          }}
        />
      )}

      {medicModalOpen && selectedAssessmentAthlete && (
        <MedicAssessmentModal
          isOpen={medicModalOpen}
          onClose={() => {
            setMedicModalOpen(false)
            setSelectedAssessmentAthlete(null)
          }}
          onSuccess={() => {
            loadData()
            setNotificationMsg('Valoración operativa médica actualizada correctamente.')
            setTimeout(() => setNotificationMsg(null), 3000)
          }}
          teamId={teamId}
          athleteId={selectedAssessmentAthlete.id}
          athleteName={selectedAssessmentAthlete.name}
          dorsalNumber={selectedAssessmentAthlete.dorsal}
          existingAssessment={selectedAssessmentAthlete.assessment}
          activeIncident={selectedAssessmentAthlete.incident}
        />
      )}

      {profileModalOpen && athleteProfile && (
        <AthleteProfileModal
          isOpen={profileModalOpen}
          onClose={() => setProfileModalOpen(false)}
          onSuccess={() => {
            loadData()
            setNotificationMsg('Perfil deportivo actualizado correctamente.')
            setTimeout(() => setNotificationMsg(null), 3000)
          }}
          profile={athleteProfile}
        />
      )}

      {scheduleModalOpen && (
        <TeamScheduleModal
          isOpen={scheduleModalOpen}
          onClose={() => setScheduleModalOpen(false)}
          onSuccess={() => {
            loadData()
            setNotificationMsg('Horario semanal de entrega actualizado correctamente.')
            setTimeout(() => setNotificationMsg(null), 3000)
          }}
          teamId={teamId}
        />
      )}

      {campDailyModalOpen && athleteId && activeDailyObligation && (
        <CampDailyCheckinModal
          isOpen={campDailyModalOpen}
          onClose={() => {
            setCampDailyModalOpen(false)
            setActiveDailyObligation(null)
            setActiveDailyResponse(null)
          }}
          onSuccess={(status) => {
            setNotificationMsg(
              status === 'submitted'
                ? '¡Check-in diario enviado correctamente al servicio médico!'
                : 'Borrador de check-in guardado online.'
            )
            setTimeout(() => setNotificationMsg(null), 4000)
            loadData()
            loadCampStaffData()
          }}
          teamId={teamId}
          athleteId={athleteId}
          eventId={activeDailyObligation.event_id || selectedCampId}
          campName={activeDailyObligation.title || 'Concentración Nacional Flag 5v5'}
          targetDate={activeDailyObligation.period_start || selectedCampDate}
          dueTime={activeDailyObligation.daily_checkin_due_time || '09:00:00'}
          existingResponse={activeDailyResponse}
          openEpisodes={incidents.filter((i) => i.status === 'open' || i.status === 'monitoring')}
          onOpenIncidentModal={() => setIncidentModalOpen(true)}
        />
      )}
    </div>
  )
}
